import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { selectAdminToken } from "../../sessionSlice";
import { uploadThemeMedia } from "../../functions/uploadThemeMedia";
import { updateThemeData } from "../../functions/updateThemeData";
import "./themeUpdate.css";

const VALID_EXT = ["jpg", "jpeg", "png"];
const MAX_AVATAR_IMAGES = 20;
const DESCRIPTION_MAX_LEN = 100;
const CHARACTER_NAME_MAX_LEN = 40;
// There's only one photobooth avatar picker, so there's no need for a
// category toggle/add/delete UI — always manage this single fixed set.
const CATEGORY = "superheros";

const TABS = [
  { id: "avatar",   label: "Avatar" },
  { id: "settings", label: "Settings" },
  { id: "manager",  label: "Manager" },
];

const BEGIN_BUTTON_SIZE = { width: 475,  height: 240 };
const HEADING_MAIN_SIZE = { width: 2113, height: 744 };
const MANAGER_NAME_MAX_LEN = 30;

const parseObject = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

/** Encode each path segment (decode first to avoid double-encoding stored keys). */
const buildHostedAssetUrl = (baseEnv, relativePath) => {
  const base = String(baseEnv ?? "").replace(/\/+$/, "");
  const raw = String(relativePath ?? "").trim().replace(/^\/+/, "");
  if (!raw) return "";
  const encoded = raw
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      try {
        return encodeURIComponent(decodeURIComponent(seg));
      } catch {
        return encodeURIComponent(seg);
      }
    })
    .join("/");
  return base ? `${base}/${encoded}` : encoded;
};

/** Prefix with numeric id so two uploads with same basename stay unique. */
const renameFileForUpload = (file) => {
  if (!file || !(file instanceof Blob)) return file;
  const original = file.name || "image";
  const lastDot = original.lastIndexOf(".");
  const ext = lastDot >= 0 ? original.slice(lastDot) : "";
  const base = lastDot >= 0 ? original.slice(0, lastDot) : original;
  const id = `${Date.now()}-${Math.floor(Math.random() * 1_000_000_000)}`;
  const newName = `${id}-${base}${ext}`;
  return new File([file], newName, {
    type: file.type || "application/octet-stream",
    lastModified: file.lastModified,
  });
};

function ThemeUpdate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { currentTheme, data } = useSelector((state) => state.theme);

  const [characterThemes, setCharacterThemes] = useState({});
  const [avatarFiles, setAvatarFiles] = useState([]);
  const [avatarBlobSrcByName, setAvatarBlobSrcByName] = useState({});
  const [avatarMeta, setAvatarMeta] = useState({}); // { [filename]: { characterName, description } }
  const [pendingMeta, setPendingMeta] = useState({}); // { [pendingFile.name]: { characterName, description } }
  const [saving, setSaving] = useState(false);
  // Replacing an existing avatar's picture (not adding a new one) — keyed
  // by the OLD filename so its saved name/description/gender and its
  // position in the list survive the swap.
  const [replacementFiles, setReplacementFiles] = useState({}); // { [oldFilename]: File }
  const [replacementBlobSrcByName, setReplacementBlobSrcByName] = useState({});
  const [replaceTarget, setReplaceTarget] = useState(null);
  const replaceFileInputRef = useRef(null);

  const [activeTab, setActiveTab] = useState("avatar");

  // ── Settings tab: begin button / heading images ────────────────────────
  const [beginButtonFile, setBeginButtonFile] = useState(null);
  const [beginButtonPreview, setBeginButtonPreview] = useState(null);
  const [headingMainFile, setHeadingMainFile] = useState(null);
  const [headingMainPreview, setHeadingMainPreview] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // ── Manager tab: CSV list vs free-text toggle ───────────────────────────
  const [managerNameMode, setManagerNameMode] = useState("text"); // "csv" | "text"
  const [managerNames, setManagerNames] = useState([]);
  const [managerCsvFileName, setManagerCsvFileName] = useState("");
  const [manualManagerName, setManualManagerName] = useState("");
  const [savingManager, setSavingManager] = useState(false);
  const managerCsvInputRef = useRef(null);

  useEffect(() => {
    if (currentTheme) {
      dispatch(fetchThemeData({ themeId: currentTheme }));
    }
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (!data) return;
    setCharacterThemes(parseObject(data.character_theme));
    setAvatarMeta(parseObject(data.avatar_meta));
    setManagerNameMode(data.manager_names_mode === "csv" ? "csv" : "text");
    setManagerNames(Array.isArray(data.manager_names) ? data.manager_names : []);
  }, [data]);

  useLayoutEffect(() => {
    const next = {};
    avatarFiles.forEach((f) => {
      if (f?.name) next[f.name] = URL.createObjectURL(f);
    });
    setAvatarBlobSrcByName(next);
    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [avatarFiles]);

  useLayoutEffect(() => {
    const next = {};
    Object.entries(replacementFiles).forEach(([oldFilename, file]) => {
      if (file) next[oldFilename] = URL.createObjectURL(file);
    });
    setReplacementBlobSrcByName(next);
    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [replacementFiles]);

  // Avatar images are hosted under an "avatar/" subfolder (matches the
  // photobooth avatar-picker's fetch path).
  const getAvatarPreviewUrl = (fileName) => {
    if (!fileName) return "";
    return buildHostedAssetUrl(process.env.REACT_APP_S3_PATH || "", `avatar/${fileName}`);
  };

  // Non-avatar theme images (logo, background, and these two) are uploaded
  // to the root of the S3 path rather than the "avatar/" subfolder — this
  // mirrors how updateThemeMedia.js on the backend stores any file field
  // that isn't prefixed avatar_/good_/bad_.
  const getRootAssetUrl = (fileName) => {
    if (!fileName) return "";
    return buildHostedAssetUrl(process.env.REACT_APP_S3_PATH || "", fileName);
  };

  /** PNG-only, exact-pixel-dimensions validation for the Settings tab images. */
  const validateExactPng = (file, { width, height }) => new Promise((resolve) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext !== "png") {
      resolve(`Please upload a PNG image (${width}x${height}px).`);
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== width || img.height !== height) {
        resolve(`Image must be exactly ${width}x${height}px (this one is ${img.width}x${img.height}px).`);
      } else {
        resolve(null);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve("Could not read that image.");
    };
    img.src = objectUrl;
  });

  const buildThemeFormData = (themesMap) => {
    const formData = new FormData();
    formData.append("themeName", currentTheme);
    formData.append(
      "extraData",
      JSON.stringify({
        themeName: data?.themeName || "",
        themeDescription: data?.themeDescription || "",
        character: themesMap[CATEGORY] || [],
        character_theme: themesMap,
        selected_character_theme: CATEGORY,
        timer: data?.timer || "05:00",
      })
    );
    return formData;
  };

  const validateAvatarImage = (file) => {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!VALID_EXT.includes(ext)) {
      return `Unsupported format for ${file.name}`;
    }
    return null;
  };

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = "";
    if (!selected.length) return;

    const existingCount = (characterThemes[CATEGORY] || []).length;
    const pendingCount = avatarFiles.length;
    const totalAfter = existingCount + pendingCount + selected.length;
    if (totalAfter > MAX_AVATAR_IMAGES) {
      Swal.fire(
        "Limit exceeded",
        `You can add up to ${MAX_AVATAR_IMAGES} avatar images, and only ${Math.max(MAX_AVATAR_IMAGES - existingCount - pendingCount, 0)} more can be added right now.`,
        "warning"
      );
      return;
    }

    const errors = selected.map(validateAvatarImage).filter(Boolean);
    if (errors.length) {
      Swal.fire("Invalid file", errors[0], "error");
      return;
    }

    const renamed = selected.map(renameFileForUpload);
    setAvatarFiles((prev) => [...prev, ...renamed]);
    setPendingMeta((prev) => {
      const next = { ...prev };
      renamed.forEach((f) => {
        next[f.name] = { characterName: "", description: "" };
      });
      return next;
    });
  };

  // ── Replace an existing avatar's picture in place ──────────────────────
  const triggerReplaceImage = (oldFilename) => {
    setReplaceTarget(oldFilename);
    replaceFileInputRef.current?.click();
  };

  const handleReplaceFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !replaceTarget) return;

    const error = validateAvatarImage(file);
    if (error) {
      Swal.fire("Invalid file", error, "error");
      setReplaceTarget(null);
      return;
    }

    setReplacementFiles((prev) => ({ ...prev, [replaceTarget]: renameFileForUpload(file) }));
    setReplaceTarget(null);
  };

  const cancelReplaceImage = (oldFilename) => {
    setReplacementFiles((prev) => {
      const { [oldFilename]: _dropped, ...rest } = prev;
      return rest;
    });
  };

  const removePendingFile = (index) => {
    setAvatarFiles((prev) => {
      const removed = prev[index];
      if (removed) {
        setPendingMeta((prevMeta) => {
          const { [removed.name]: _dropped, ...rest } = prevMeta;
          return rest;
        });
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const removeExistingImage = (index) => {
    setCharacterThemes((prev) => {
      const removedName = (prev[CATEGORY] || [])[index];
      if (removedName) {
        setAvatarMeta((prevMeta) => {
          const { [removedName]: _dropped, ...rest } = prevMeta;
          return rest;
        });
      }
      return {
        ...prev,
        [CATEGORY]: (prev[CATEGORY] || []).filter((_, i) => i !== index),
      };
    });
  };

  const updateAvatarMetaField = (filename, field, value) => {
    setAvatarMeta((prev) => ({
      ...prev,
      [filename]: { ...prev[filename], [field]: value },
    }));
  };

  const updatePendingMetaField = (pendingKey, field, value) => {
    setPendingMeta((prev) => ({
      ...prev,
      [pendingKey]: { ...prev[pendingKey], [field]: value },
    }));
  };

  const handleSave = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    setSaving(true);
    try {
      const replacementEntries = Object.entries(replacementFiles); // [[oldFilename, file], ...]

      const formData = buildThemeFormData(characterThemes);
      avatarFiles.forEach((file) => formData.append("avatarFiles", file));
      // Replacement images ride along in the same upload call — the backend
      // always appends new uploads to the tail of the array, so they land
      // in the wrong spot at first; the correction below moves each one
      // back into its original avatar's position right after.
      replacementEntries.forEach(([, file]) => formData.append("avatarFiles", file));

      await uploadThemeMedia({ formData, token: adminToken });
      const refreshed = await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      // Newly uploaded files get a server-assigned filename we can't predict
      // client-side, so map the pending metadata onto the tail of the
      // freshly-saved image list (uploads are always appended in order —
      // new avatars first, then replacements, matching the append order
      // above).
      const nextAvatarMeta = { ...avatarMeta };
      const totalUploaded = avatarFiles.length + replacementEntries.length;
      let correctedThemeArray = null;

      if (totalUploaded) {
        const newThemes = parseObject(refreshed?.data?.character_theme);
        const newImages = newThemes[CATEGORY] || [];
        const addedFilenames = newImages.slice(-totalUploaded);

        const newlyAddedFilenames = addedFilenames.slice(0, avatarFiles.length);
        newlyAddedFilenames.forEach((filename, i) => {
          const pendingFile = avatarFiles[i];
          const meta = pendingFile ? pendingMeta[pendingFile.name] : null;
          if (meta && (meta.characterName || meta.description)) {
            nextAvatarMeta[filename] = { ...meta };
          }
        });

        if (replacementEntries.length) {
          const replacementNewFilenames = addedFilenames.slice(avatarFiles.length);
          // Drop the replacement uploads off the tail — they belong at
          // their original avatar's index, not appended as new entries.
          correctedThemeArray = newImages.slice(0, newImages.length - replacementEntries.length);

          replacementEntries.forEach(([oldFilename], i) => {
            const newFilename = replacementNewFilenames[i];
            if (!newFilename) return;
            const idx = correctedThemeArray.indexOf(oldFilename);
            if (idx !== -1) {
              correctedThemeArray[idx] = newFilename;
            } else {
              correctedThemeArray.push(newFilename);
            }
            if (nextAvatarMeta[oldFilename]) {
              nextAvatarMeta[newFilename] = nextAvatarMeta[oldFilename];
              delete nextAvatarMeta[oldFilename];
            }
          });

          await uploadThemeMedia({
            formData: buildThemeFormData({ ...newThemes, [CATEGORY]: correctedThemeArray }),
            token: adminToken,
          });
        }
      }

      await updateThemeData({
        payload: { data: { avatar_meta: nextAvatarMeta }, currentTheme },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      setAvatarFiles([]);
      setPendingMeta({});
      setReplacementFiles({});
      Swal.fire("Success", "Theme data updated", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update theme data", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Settings tab ────────────────────────────────────────────────────────
  const handleBeginButtonChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const error = await validateExactPng(file, BEGIN_BUTTON_SIZE);
    if (error) {
      Swal.fire("Invalid image", error, "error");
      return;
    }
    setBeginButtonFile(file);
    setBeginButtonPreview(URL.createObjectURL(file));
  };

  const handleHeadingMainChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const error = await validateExactPng(file, HEADING_MAIN_SIZE);
    if (error) {
      Swal.fire("Invalid image", error, "error");
      return;
    }
    setHeadingMainFile(file);
    setHeadingMainPreview(URL.createObjectURL(file));
  };

  const handleSaveSettings = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }
    if (!beginButtonFile && !headingMainFile) {
      Swal.fire("Nothing to save", "Choose a new image first.", "info");
      return;
    }

    setSavingSettings(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName || "",
          themeDescription: data?.themeDescription || "",
        })
      );
      if (beginButtonFile) formData.append("beginButtonImage", beginButtonFile);
      if (headingMainFile) formData.append("headingMainImage", headingMainFile);

      await uploadThemeMedia({ formData, token: adminToken });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();

      setBeginButtonFile(null);
      setBeginButtonPreview(null);
      setHeadingMainFile(null);
      setHeadingMainPreview(null);
      Swal.fire("Success", "Settings updated", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update settings", "error");
    } finally {
      setSavingSettings(false);
    }
  };

  // ── Manager tab ─────────────────────────────────────────────────────────
  const SAMPLE_CSV_ROWS = ["name", "Alice Smith", "Bob Johnson", "Priya Patel"];
  const HEADER_ROW_PATTERN = /^(name|manager|manager[ _]?name)$/i;

  const handleDownloadSampleCsv = () => {
    const blob = new Blob([SAMPLE_CSV_ROWS.join("\r\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "manager-names-sample.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleManagerCsvChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (ext !== "csv") {
      Swal.fire("Invalid file", "Please upload a .csv file.", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      let lines = text.split(/\r?\n/).map((line) => line.split(",")[0]?.trim()).filter(Boolean);

      // The sample CSV (and many spreadsheet exports) start with a "name"
      // header row — drop it if the first line looks like a label rather
      // than an actual manager name, instead of importing it as one.
      if (lines.length && HEADER_ROW_PATTERN.test(lines[0])) {
        lines = lines.slice(1);
      }

      const names = lines.map((name) => name.slice(0, MANAGER_NAME_MAX_LEN));

      if (names.length === 0) {
        Swal.fire("Empty file", "No manager names found in that CSV.", "warning");
        return;
      }
      setManagerNames(names);
      setManagerCsvFileName(file.name);
    };
    reader.onerror = () => Swal.fire("Error", "Could not read that CSV file.", "error");
    reader.readAsText(file);
  };

  const removeManagerName = (index) => {
    setManagerNames((prev) => prev.filter((_, i) => i !== index));
  };

  const clearManagerCsv = () => {
    setManagerNames([]);
    setManagerCsvFileName("");
  };

  const handleAddManualManagerName = () => {
    const name = manualManagerName.trim().slice(0, MANAGER_NAME_MAX_LEN);
    if (!name) return;
    if (managerNames.some((n) => n.toLowerCase() === name.toLowerCase())) {
      Swal.fire("Already added", `"${name}" is already in the list.`, "warning");
      return;
    }
    setManagerNames((prev) => [...prev, name]);
    setManualManagerName("");
  };

  const handleSaveManager = async () => {
    if (!currentTheme) {
      Swal.fire("Error", "No theme selected", "error");
      return;
    }

    setSavingManager(true);
    try {
      await updateThemeData({
        payload: {
          data: {
            manager_names_mode: managerNameMode,
            manager_names: managerNameMode === "csv" ? managerNames : [],
          },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      Swal.fire("Success", "Manager settings updated", "success");
    } catch (error) {
      Swal.fire("Error", error.message || "Failed to update manager settings", "error");
    } finally {
      setSavingManager(false);
    }
  };

  const activeImages = characterThemes[CATEGORY] || [];

  return (
    <>
      <div className="back-button-holder">
        <button className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="theme-tabs-row">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`theme-tab-btn${activeTab === tab.id ? " theme-tab-btn-active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="theme-data-page">
        <div className="theme-data-grid">
          {activeTab === "avatar" && (
          <section className="theme-data-card">
            <h3>Avatar Character Set</h3>

            <input
              type="file"
              accept=".jpg,.jpeg,.png"
              multiple
              onChange={handleFileChange}
            />
            <input
              ref={replaceFileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={handleReplaceFileChange}
              style={{ display: "none" }}
            />
            <div className="theme-data-helper-text">JPG/JPEG/PNG</div>
            <div className="theme-data-helper-text">Maximum {MAX_AVATAR_IMAGES} avatar images.</div>
            {avatarFiles.length > 0 && (
              <div className="theme-data-pending">
                Pending upload: {avatarFiles.map((f) => f.name).join(", ")}
              </div>
            )}

            <div className="theme-data-list">
              {activeImages.map((name, index) => {
                const hasReplacement = Object.prototype.hasOwnProperty.call(replacementFiles, name);
                return (
                    <div className={`theme-data-item${hasReplacement ? " theme-data-item-replacing" : ""}`} key={`${name}-${index}`}>
                      <div className="theme-data-item-img-wrap">
                        <img src={hasReplacement ? replacementBlobSrcByName[name] : getAvatarPreviewUrl(name)} alt={name} />
                      </div>
                      <span title={name}>{name}</span>
                      <button onClick={() => removeExistingImage(index)}>&times;</button>
                      {hasReplacement ? (
                        <div className="theme-data-item-replace-row">
                          <span className="theme-data-item-replace-note">New photo (pending save)</span>
                          <button
                            type="button"
                            className="theme-data-item-replace-cancel"
                            onClick={() => cancelReplaceImage(name)}
                          >
                            Undo
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="theme-data-item-replace-btn"
                          onClick={() => triggerReplaceImage(name)}
                        >
                          Change Photo
                        </button>
                      )}
                      <input
                        type="text"
                        className="theme-data-item-name"
                        placeholder="Character name (e.g. Captain Clarity)"
                        maxLength={CHARACTER_NAME_MAX_LEN}
                        value={avatarMeta[name]?.characterName || ""}
                        onChange={(e) => updateAvatarMetaField(name, "characterName", e.target.value)}
                      />
                      <textarea
                        className="theme-data-item-desc"
                        placeholder="Superpower description"
                        maxLength={DESCRIPTION_MAX_LEN}
                        value={avatarMeta[name]?.description || ""}
                        onChange={(e) => updateAvatarMetaField(name, "description", e.target.value)}
                      />
                      <div className="theme-data-item-count">
                        {(avatarMeta[name]?.description || "").length}/{DESCRIPTION_MAX_LEN}
                      </div>
                      <select
                        className="theme-data-item-gender"
                        value={avatarMeta[name]?.gender || ""}
                        onChange={(e) => updateAvatarMetaField(name, "gender", e.target.value)}
                      >
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                      </select>
                    </div>
                );
              })}
                  {avatarFiles.map((file, index) => (
                    <div className="theme-data-item theme-data-item-pending" key={`pending-${file.name}-${index}`}>
                      <div className="theme-data-item-img-wrap">
                        <img src={avatarBlobSrcByName[file.name]} alt={file.name} />
                      </div>
                      <span title={file.name}>{file.name}</span>
                      <button onClick={() => removePendingFile(index)}>&times;</button>
                      <input
                        type="text"
                        className="theme-data-item-name"
                        placeholder="Character name (e.g. Captain Clarity)"
                        maxLength={CHARACTER_NAME_MAX_LEN}
                        value={pendingMeta[file.name]?.characterName || ""}
                        onChange={(e) => updatePendingMetaField(file.name, "characterName", e.target.value)}
                      />
                      <textarea
                        className="theme-data-item-desc"
                        placeholder="Superpower description"
                        maxLength={DESCRIPTION_MAX_LEN}
                        value={pendingMeta[file.name]?.description || ""}
                        onChange={(e) => updatePendingMetaField(file.name, "description", e.target.value)}
                      />
                      <div className="theme-data-item-count">
                        {(pendingMeta[file.name]?.description || "").length}/{DESCRIPTION_MAX_LEN}
                      </div>
                      <select
                        className="theme-data-item-gender"
                        value={pendingMeta[file.name]?.gender || ""}
                        onChange={(e) => updatePendingMetaField(file.name, "gender", e.target.value)}
                      >
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                      </select>
                    </div>
                  ))}
                </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </section>
          )}

          {activeTab === "settings" && (
          <section className="theme-data-card">
            <h3>Settings</h3>

            <div className="theme-settings-image-row">
              <div className="theme-settings-image-field">
                <label className="theme-settings-image-label">
                  Begin Button ({BEGIN_BUTTON_SIZE.width}x{BEGIN_BUTTON_SIZE.height}px, PNG only)
                </label>
                <div className="theme-settings-image-preview">
                  {(beginButtonPreview || data?.beginButtonImage) ? (
                    <img
                      src={beginButtonPreview || getRootAssetUrl(data.beginButtonImage)}
                      alt="Begin button"
                    />
                  ) : (
                    <span className="theme-settings-image-empty">No image uploaded yet</span>
                  )}
                </div>
                <input type="file" accept=".png" onChange={handleBeginButtonChange} />
              </div>

              <div className="theme-settings-image-field">
                <label className="theme-settings-image-label">
                  Heading Main ({HEADING_MAIN_SIZE.width}x{HEADING_MAIN_SIZE.height}px, PNG only)
                </label>
                <div className="theme-settings-image-preview">
                  {(headingMainPreview || data?.headingMainImage) ? (
                    <img
                      src={headingMainPreview || getRootAssetUrl(data.headingMainImage)}
                      alt="Heading main"
                    />
                  ) : (
                    <span className="theme-settings-image-empty">No image uploaded yet</span>
                  )}
                </div>
                <input type="file" accept=".png" onChange={handleHeadingMainChange} />
              </div>
            </div>

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSaveSettings} disabled={savingSettings}>
                {savingSettings ? "Saving..." : "Save"}
              </button>
            </div>
          </section>
          )}

          {activeTab === "manager" && (
          <section className="theme-data-card">
            <h3>Manager</h3>

            <div className="gallery-toggle-row">
              <span>
                {managerNameMode === "csv"
                  ? "Managers are picked from an uploaded name list"
                  : "Managers type their own name"}
              </span>
              <label className="theme-toggle">
                <input
                  type="checkbox"
                  checked={managerNameMode === "csv"}
                  onChange={(e) => setManagerNameMode(e.target.checked ? "csv" : "text")}
                />
                <span className="theme-toggle-slider" />
              </label>
            </div>

            {managerNameMode === "csv" ? (
              <>
                <div className="theme-data-helper-text">
                  Upload a CSV of manager names (one per line, or name in the first column — a "name" header row is fine too). Maximum {MANAGER_NAME_MAX_LEN} characters per name.
                </div>

                <div className="theme-csv-upload-row">
                  <input
                    ref={managerCsvInputRef}
                    type="file"
                    accept=".csv"
                    onChange={handleManagerCsvChange}
                    style={{ display: "none" }}
                  />
                  <button
                    type="button"
                    className="theme-csv-upload-btn"
                    onClick={() => managerCsvInputRef.current?.click()}
                  >
                    📄 Upload CSV
                  </button>
                  <button
                    type="button"
                    className="theme-csv-sample-btn"
                    onClick={handleDownloadSampleCsv}
                  >
                    ⬇ Download Sample CSV
                  </button>
                </div>

                <div className="theme-manual-add-row">
                  <input
                    type="text"
                    className="theme-manual-add-input"
                    placeholder="Add a manager name manually"
                    maxLength={MANAGER_NAME_MAX_LEN}
                    value={manualManagerName}
                    onChange={(e) => setManualManagerName(e.target.value.slice(0, MANAGER_NAME_MAX_LEN))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddManualManagerName();
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="theme-manual-add-btn"
                    onClick={handleAddManualManagerName}
                    disabled={!manualManagerName.trim()}
                  >
                    + Add
                  </button>
                </div>

                {managerCsvFileName && (
                  <div className="theme-data-pending">
                    Loaded from <strong>{managerCsvFileName}</strong> — {managerNames.length} name{managerNames.length === 1 ? "" : "s"}
                    {managerNames.length > 0 && (
                      <button type="button" className="theme-csv-clear-btn" onClick={clearManagerCsv}>
                        Clear
                      </button>
                    )}
                  </div>
                )}

                {managerNames.length > 0 ? (
                  <div className="theme-manager-name-list">
                    {managerNames.map((n, i) => (
                      <div className="theme-manager-name-chip" key={`${n}-${i}`}>
                        <span title={n}>{n}</span>
                        <button type="button" onClick={() => removeManagerName(i)}>&times;</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="theme-data-helper-text">No names uploaded yet.</div>
                )}
              </>
            ) : (
              <div className="theme-data-helper-text">
                No list needed — managers will type their name directly on the avatar page, as normal.
              </div>
            )}

            <div className="theme-data-actions">
              <button className="save-and-continue" onClick={handleSaveManager} disabled={savingManager}>
                {savingManager ? "Saving..." : "Save"}
              </button>
            </div>
          </section>
          )}
        </div>
      </div>
    </>
  );
}

export default ThemeUpdate;
